import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { siteImages } from "@/lib/siteData";
import { supabase } from "@/integrations/supabase/client";

type Product = {
  id: string;
  name: string;
  slug: string;
  price: number;
  images: unknown;
  category: string | null;
};

const fallback: Product[] = [
  { id: "f1", name: "Beauty Station", slug: "beauty-station", price: 25, images: ["/__l5e/assets-v1/2bba9a3e-18c8-447a-acd2-7f485e8d2ea3/BeautificationStation_BrianaSharae-scaled.jpg"], category: null },
  { id: "f2", name: "Studio A", slug: "studio-a", price: 45, images: ["/__l5e/assets-v1/16f70672-6eb2-4d50-809d-8650776cb94e/MyStudio_Instagram11.jpg"], category: null },
  { id: "f3", name: "Studio B", slug: "studio-b", price: 70, images: ["/__l5e/assets-v1/a7d612c9-f9e4-49ea-b6c6-72eed423f218/StudioB_BTSStacey.jpg"], category: null },
  { id: "f4", name: "Headshot Photography", slug: "headshot", price: 150, images: ["/__l5e/assets-v1/b58a85ac-4062-46ee-a8e5-d3b55773cc0f/jess-headshots-final-3-scaled.jpg"], category: null },
];

const firstImage = (imgs: unknown): string => {
  if (Array.isArray(imgs) && imgs.length > 0 && typeof imgs[0] === "string") return imgs[0];
  return siteImages.consultationImage;
};

const ShopPage = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.title = "Shop | MyStudio";
    supabase
      .from("products")
      .select("id,name,slug,price,images,category")
      .eq("active", true)
      .order("display_order", { ascending: true })
      .then(({ data }) => {
        setProducts(data && data.length > 0 ? (data as Product[]) : fallback);
        setLoading(false);
      });
  }, []);

  const jsonLd = useMemo(
    () => ({
      itemList: {
        "@context": "https://schema.org",
        "@type": "ItemList",
        name: "MyStudio Shop",
        itemListElement: products.map((p, i) => ({
          "@type": "ListItem",
          position: i + 1,
          url: `https://bookmystudiospace.com/shop/${p.slug}`,
          name: p.name,
        })),
      },
      breadcrumb: {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: "https://bookmystudiospace.com/" },
          { "@type": "ListItem", position: 2, name: "Shop", item: "https://bookmystudiospace.com/shop" },
        ],
      },
    }),
    [products]
  );

  return (
    <div className="pt-20">
      <Helmet>
        <title>Shop | MyStudio Columbus GA</title>
        <meta name="description" content="Book studio sessions, beauty station add-ons, headshots, and MyStudio merch — Columbus, GA's photo & video studio." />
        <link rel="canonical" href="https://bookmystudiospace.com/shop" />
        <meta property="og:title" content="Shop | MyStudio Columbus GA" />
        <meta property="og:description" content="Studio sessions, add-ons, and MyStudio merch." />
        <meta property="og:url" content="https://bookmystudiospace.com/shop" />
        <meta property="og:type" content="website" />
        <script type="application/ld+json">{JSON.stringify(jsonLd.itemList)}</script>
        <script type="application/ld+json">{JSON.stringify(jsonLd.breadcrumb)}</script>
      </Helmet>
      <section className="relative h-[40vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${siteImages.heroSlider1})` }} />
        <div className="absolute inset-0 bg-background/70" />
        <div className="relative z-10 text-center">
          <h1 className="text-4xl md:text-6xl font-display font-extrabold uppercase tracking-widest">SHOP</h1>
        </div>
      </section>

      <section className="py-16">
        <div className="container mx-auto px-4">
          {loading ? (
            <p className="text-center text-muted-foreground font-mono">Loading…</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {products.map((p) => (
                <Link key={p.id} to={`/shop/${p.slug}`} className="group bg-panel border border-border overflow-hidden hover:glow-amber transition-all">
                  <div className="aspect-square bg-muted overflow-hidden">
                    <img src={firstImage(p.images)} alt={`${p.name}${p.category ? ` — ${p.category}` : ""} at MyStudio`} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                  </div>
                  <div className="p-4 text-center">
                    <h3 className="font-body font-medium text-sm mb-1">{p.name}</h3>
                    <p className="font-mono text-primary text-sm">${Number(p.price).toFixed(2)}</p>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default ShopPage;
